'use client'

import Script from 'next/script'

/**
 * MIST-0804 client token acquisition.
 *
 * Uses Cloudflare's implicit rendering: the script finds `.cf-turnstile` and
 * injects a hidden `cf-turnstile-response` input into the enclosing <form>, so
 * the token arrives through normal FormData with no imperative callback state.
 *
 * Degradation is the point (spec.md:484). With no site key configured, a blocked
 * script, or no JavaScript at all, this contributes nothing and the form still
 * submits — the API then grades the request `missing` and throttles instead of
 * blocking. Rendering nothing is a supported state, never an error.
 */

export interface TurnstileWidgetProps {
  /** Must match the action the API verifies for this route. */
  readonly action: string
  /** Defaults to the build-inlined public key; injectable for tests. */
  readonly siteKey?: string | undefined
}

export default function TurnstileWidget({ action, siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY }: TurnstileWidgetProps) {
  if (!siteKey) return null
  return (
    <>
      <div
        className="cf-turnstile"
        data-sitekey={siteKey}
        data-action={action}
        // Only interrupts when Cloudflare actually suspects automation, so the
        // common path stays a zero-interaction, zero-layout-shift form.
        data-appearance="interaction-only"
        data-theme="dark"
      />
      <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="lazyOnload" />
    </>
  )
}
